/* ============================================================
 * <lion-variant-picker>
 * - Reads variants JSON embedded in <script data-variants-json>
 * - On change, finds matching variant, updates URL (history),
 *   fetches section HTML via ?variant=... to refresh price /
 *   availability / SKU / gallery media, updates form hidden id.
 * ============================================================ */

class VariantPicker extends HTMLElement {
  constructor() {
    super();
    const jsonEl = this.querySelector('[data-variants-json]');
    this.variants = jsonEl ? JSON.parse(jsonEl.textContent) : [];
    this.sectionId = this.dataset.section;
    this.productUrl = this.dataset.url;
    this.addEventListener('change', this.onChange.bind(this));
    this.updateSelectedLabels();
    this.updateAvailability();
    this.setPurchaseAvailability(this.findVariant(this.selectedValues));
  }

  get selectedValues() {
    return [...this.querySelectorAll('fieldset')].map((fs) => {
      const select = fs.querySelector('[data-option-value-select]');
      if (select) return select.value || null;
      return fs.querySelector('[data-option-value]:checked')?.value || null;
    });
  }

  async onChange() {
    this.updateSelectedLabels();
    this.updateAvailability();
    const current = this.findVariant(this.selectedValues);
    this.setPurchaseAvailability(current);
    if (!current) {
      window.publish?.(window.PUB_SUB_EVENTS.variantChange, { variant: null });
      return;
    }

    // Update URL without reload
    const url = new URL(window.location.href);
    url.searchParams.set('variant', current.id);
    history.replaceState({}, '', url);

    this.updateHiddenFormId(current.id);
    this.swapGalleryMedia(current);
    window.publish?.(window.PUB_SUB_EVENTS.variantChange, { variant: current });

    await this.refreshSection(current.id);
  }

  findVariant(selectedValues) {
    return this.variants.find((v) =>
      v.options.every((opt, i) => opt === selectedValues[i])
    );
  }

  updateSelectedLabels() {
    this.querySelectorAll('fieldset').forEach((fs) => {
      const select = fs.querySelector('[data-option-value-select]');
      const value = select
        ? select.value
        : fs.querySelector('[data-option-value]:checked')?.value;
      const target = fs.querySelector('[data-option-selected]');
      if (target && value) target.textContent = value;
    });
  }

  updateAvailability() {
    const fieldsets = [...this.querySelectorAll('fieldset')];
    const selectedValues = this.selectedValues;
    const soldOutLabel = window.lionStrings?.soldOut || 'Sold out';

    fieldsets.forEach((fs, optionIndex) => {
      const select = fs.querySelector('[data-option-value-select]');
      if (select) {
        [...select.options].forEach((option) => {
          const isAvailable = this.isOptionValueAvailable(
            optionIndex,
            option.value,
            selectedValues
          );
          option.disabled = !isAvailable;
          const baseLabel = option.value;
          option.textContent = isAvailable
            ? baseLabel
            : `${baseLabel} — ${soldOutLabel}`;
        });
        return;
      }

      fs.querySelectorAll('[data-option-value]').forEach((input) => {
        const isAvailable = this.isOptionValueAvailable(
          optionIndex,
          input.value,
          selectedValues
        );
        input.dataset.available = isAvailable;
        input.disabled = !isAvailable;

        const label = input.nextElementSibling;
        if (!label) return;
        label.classList.toggle('product__option-value--sold-out', !isAvailable);
        label.title = isAvailable ? input.value : `${input.value} — ${soldOutLabel}`;
      });
    });
  }

  isOptionValueAvailable(optionIndex, value, selectedValues) {
    return this.variants.some((variant) => {
      if (!variant.available || variant.options[optionIndex] !== value) return false;
      return variant.options.every((opt, i) => i === optionIndex || opt === selectedValues[i]);
    });
  }

  updateHiddenFormId(variantId) {
    const form = document.querySelector(`#product-form-${this.sectionId}`);
    if (!form) return;
    const idInput = form.querySelector('[name="id"]');
    if (idInput) idInput.value = variantId;
  }

  swapGalleryMedia(variant) {
    if (!variant.featured_media) return;
    // Scope to THIS picker's product so a quick-add modal (or any second
    // product section on the page) can't swap the wrong gallery.
    const productId = this.dataset.productId;
    const scope = productId
      ? document.querySelector(`[data-product-id="${productId}"]`)
      : document;
    const gallery = scope?.querySelector('lion-product-gallery');
    gallery?.selectByMediaId?.(variant.featured_media.id);
  }

  setPurchaseAvailability(variant) {
    const canPurchase = Boolean(variant && variant.available);
    this.disableAddButton(!canPurchase, variant);
    this.toggleDynamicCheckout(canPurchase);
  }

  disableAddButton(disabled, variant) {
    const btn = document.querySelector(`#product-form-${this.sectionId} [name="add"]`);
    if (!btn) return;
    btn.disabled = disabled;
    if (disabled) {
      btn.setAttribute('aria-disabled', 'true');
    } else {
      btn.removeAttribute('aria-disabled');
    }
    const label = btn.querySelector('[data-product-form-submit-label], span');
    if (label) {
      if (!disabled) {
        label.textContent = window.lionStrings?.addToCart || 'Add to cart';
      } else if (variant) {
        label.textContent = window.lionStrings?.soldOut || 'Sold out';
      } else {
        label.textContent = window.lionStrings?.unavailable || 'Unavailable';
      }
    }
  }

  toggleDynamicCheckout(canPurchase) {
    const form = document.querySelector(`#product-form-${this.sectionId}`);
    if (!form) return;
    form.querySelectorAll('.product__dynamic-checkout, .shopify-payment-button').forEach((el) => {
      el.toggleAttribute('hidden', !canPurchase);
      el.style.display = canPurchase ? '' : 'none';
      el.style.pointerEvents = canPurchase ? '' : 'none';
    });
  }

  async refreshSection(variantId) {
    try {
      const res = await fetch(`${this.productUrl}?variant=${variantId}&section_id=${this.sectionId}`);
      if (!res.ok) throw new Error(res.statusText);
      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Scope all live-DOM updates to THIS picker's product. Without this, a
      // second product section on the page (or an open quick-add modal) whose
      // markup uses the same class names would be overwritten instead.
      const productId = this.dataset.productId;
      const scope = productId
        ? document.querySelector(`[data-product-id="${productId}"]`)
        : document;
      if (!scope) return;

      // Price. PDP/featured sections use `.product__price`; the quick-add
      // modal uses `.quick-add__price`. Match either on both sides.
      const priceSelector = '.product__price, .quick-add__price';
      const priceSource = doc.querySelector(priceSelector);
      const priceTarget = scope.querySelector(priceSelector);
      if (priceSource && priceTarget) priceTarget.innerHTML = priceSource.innerHTML;

      const formSource = doc.querySelector('lion-product-form.product__form');
      const formTarget = scope.querySelector('lion-product-form.product__form');
      if (formSource && formTarget) {
        formTarget.className = formSource.className;
        formTarget.removeAttribute('hidden');
      }

      // Add-to-cart button state
      const btnSource = doc.querySelector('.product__add-to-cart');
      const btnTarget = scope.querySelector('.product__add-to-cart');
      if (btnSource && btnTarget) {
        btnTarget.innerHTML = btnSource.innerHTML;
        btnTarget.disabled = btnSource.disabled;
        if (btnSource.hasAttribute('aria-disabled') || btnSource.disabled) {
          btnTarget.setAttribute('aria-disabled', 'true');
        } else {
          btnTarget.removeAttribute('aria-disabled');
        }
      }

      const checkoutSource = doc.querySelector('.product__dynamic-checkout');
      const checkoutTarget = scope.querySelector('.product__dynamic-checkout');
      if (checkoutSource && checkoutTarget) {
        checkoutTarget.toggleAttribute('hidden', checkoutSource.hasAttribute('hidden'));
        checkoutTarget.style.display = checkoutSource.hasAttribute('hidden') ? 'none' : '';
        checkoutTarget.style.pointerEvents = checkoutSource.hasAttribute('hidden') ? 'none' : '';
      }

      // SKU / inventory
      const skuSource = doc.querySelector('[data-sku]');
      const skuTarget = scope.querySelector('[data-sku]');
      if (skuSource && skuTarget) skuTarget.textContent = skuSource.textContent;

      const invSource = doc.querySelector('[data-inventory]');
      const invTarget = scope.querySelector('[data-inventory]');
      if (invSource && invTarget) invTarget.innerHTML = invSource.innerHTML;

      // Pickup availability
      const pickupSource = doc.querySelector('[data-pickup-availability]');
      const pickupTarget = scope.querySelector('[data-pickup-availability]');
      if (pickupSource && pickupTarget) pickupTarget.innerHTML = pickupSource.innerHTML;

      // Stock notifier
      const stockSource = doc.querySelector('lion-stock-notifier');
      const stockTarget = scope.querySelector('lion-stock-notifier');
      if (stockSource && stockTarget) {
        stockTarget.outerHTML = stockSource.outerHTML;
      } else if (!stockSource && stockTarget) {
        stockTarget.hidden = true;
      }
    } catch (err) {
      console.error('[lion-variant-picker] refresh failed:', err);
    }
  }
}

if (!customElements.get('lion-variant-picker')) {
  customElements.define('lion-variant-picker', VariantPicker);
}
